import { parseCSVAuditResponse } from '../agents/auditAgent';
import { parseCSVQualityCheckResponse } from '../agents/qualityCheckAgent';
import { parseOutput } from '../agents/semanticMatchService';
import { isCommercial } from '../../config/appMode';

const DB_NAME = 'TransBCTN_Persistence';
const STORE_NAME = 'handles';
const HANDLE_KEY = 'workspace_handle';

export interface FileSystemStatus {
    isConnected: boolean;
    folderName?: string;
    error?: string;
}

/**
 * Disk Format Contract -- all parts of a single file on disk.
 * See useAutoSave.ts for save-side contract.
 *
 * Load priority:
 *   audit:    .audit.json > .audit.csv > legacy .json
 *   semantic: .semantic_match.json > .semantic_match.csv > legacy
 *   state:    .state.json > legacy .json
 */
interface FileParts {
    state?: any;               // {base}.state.json -- metadata (no heavy data)
    source?: string;           // {base}.source.txt -- plain text
    translation?: string;      // {base}.translation.txt -- newline-separated
    auditJson?: any;           // {base}.audit.json -- parsed JSON (preferred)
    auditCsv?: string;         // {base}.audit.csv -- raw CSV (legacy)
    semanticJson?: any;        // {base}.semantic_match.json -- parsed JSON (preferred)
    semanticCsv?: string;      // {base}.semantic_match.csv -- raw CSV (legacy)
    legacy?: any;              // file_{id}.json -- old all-in-one format
    originalFile?: ArrayBuffer; // {base}.original.docx/.original.pptx -- binary
    paragraphMapJson?: any;    // {base}.paragraphMap.json
}

class FileSystemPersistenceService {
    public handle: FileSystemDirectoryHandle | null = null;
    private logsCleanupDone = false;

    /**
     * Commercial-only: remove legacy `logs/` folder left from older builds that
     * wrote debug logs to customer disks. Idempotent + once-per-session.
     */
    async cleanupLegacyLogs(): Promise<void> {
        if (!isCommercial) return;
        if (this.logsCleanupDone) return;
        if (!this.handle) return;
        this.logsCleanupDone = true;
        try {
            await (this.handle as any).removeEntry('logs', { recursive: true });
        } catch {
            // Folder doesn't exist or can't be removed — ignore
        }
    }

    /**
     * Initialize and try to restore handle from IndexedDB
     */
    async init(): Promise<FileSystemStatus> {
        try {
            this.handle = await this.getHandleFromDB();
            if (this.handle) {
                // Verify permission (browser will still ask on first access)
                let permission = await (this.handle as any).queryPermission({ mode: 'readwrite' });
                if (permission === 'granted') {
                    return { isConnected: true, folderName: this.handle.name };
                }
                // If permission is 'prompt', try to request it
                // Note: This may fail without user gesture in some browsers
                if (permission === 'prompt') {
                    try {
                        permission = await (this.handle as any).requestPermission({ mode: 'readwrite' });
                        if (permission === 'granted') {
                            return { isConnected: true, folderName: this.handle.name };
                        }
                    } catch {
                        // requestPermission failed (no user gesture) - will retry on user action
                    }
                }
            }
            return { isConnected: false };
        } catch (err: any) {
            console.error('Failed to init FileSystemPersistence:', err);
            return { isConnected: false, error: err.message };
        }
    }

    /**
     * Prompt user to pick a folder
     */
    async connect(): Promise<FileSystemStatus> {
        try {
            const handle = await (window as any).showDirectoryPicker({
                mode: 'readwrite'
            });
            this.handle = handle;
            await this.saveHandleToDB(handle);
            return { isConnected: true, folderName: handle.name };
        } catch (err: any) {
            if (err.name === 'AbortError') return { isConnected: false };
            console.error('Failed to connect to folder:', err);
            return { isConnected: false, error: err.message };
        }
    }

    /**
     * Request permission for existing handle
     */
    async requestPermission(): Promise<boolean> {
        if (!this.handle) return false;
        const permission = await (this.handle as any).requestPermission({ mode: 'readwrite' });
        return permission === 'granted';
    }

    /**
     * Save a JSON object to a path
     */
    async saveJson(path: string[], fileName: string, data: any): Promise<void> {
        if (!this.handle) throw new Error('Not connected to file system');

        let currentDir = this.handle;
        for (const segment of path) {
            currentDir = await currentDir.getDirectoryHandle(segment, { create: true });
        }

        const fileHandle = await currentDir.getFileHandle(fileName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(JSON.stringify(data, null, 2));
        await writable.close();
    }

    /**
     * Save raw string content to a file
     */
    async saveFile(path: string[], fileName: string, content: string): Promise<void> {
        if (!this.handle) throw new Error('Not connected to file system');

        let currentDir = this.handle;
        for (const segment of path) {
            currentDir = await currentDir.getDirectoryHandle(segment, { create: true });
        }

        const fileHandle = await currentDir.getFileHandle(fileName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(content);
        await writable.close();
    }

    /**
     * Save binary ArrayBuffer content to a file (e.g. .docx)
     */
    async saveBinary(path: string[], fileName: string, buffer: ArrayBuffer): Promise<void> {
        if (!this.handle) throw new Error('Not connected to file system');

        let currentDir = this.handle;
        for (const segment of path) {
            currentDir = await currentDir.getDirectoryHandle(segment, { create: true });
        }

        const fileHandle = await currentDir.getFileHandle(fileName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(buffer);
        await writable.close();
    }

    /**
     * Read a binary file back as ArrayBuffer
     */
    async readBinaryFile(dirHandle: FileSystemDirectoryHandle, fileName: string): Promise<ArrayBuffer | null> {
        try {
            const fileHandle = await dirHandle.getFileHandle(fileName);
            const file = await fileHandle.getFile();
            return await file.arrayBuffer();
        } catch {
            return null;
        }
    }

    /**
     * Specific helper to save debug logs
     */
    async saveLog(log: any): Promise<void> {
        if (isCommercial) return; // Privacy: never write debug logs to customer mounted folder
        if (!this.handle) return; // Silent skip if no FS connected

        try {
            // Sanitize label for filename
            const cleanLabel = (log.label || 'log').replace(/[^a-z0-9]/gi, '_').toLowerCase();
            const fileName = `${log.id}_${cleanLabel}.json`;

            await this.saveJson(['logs'], fileName, log);
        } catch (err) {
            console.error('Failed to save log to file system:', err);
        }
    }

    /**
     * Read all debug logs from the logs/ folder
     */
    async loadLogs(): Promise<any[]> {
        if (!this.handle) return [];
        const logs: any[] = [];

        try {
            const logsDir = await this.handle.getDirectoryHandle('logs', { create: false });
            for await (const entry of (logsDir as any).values()) {
                if (entry.kind === 'file' && entry.name.endsWith('.json')) {
                    const logData = await this.readFile(logsDir, entry.name);
                    if (logData) logs.push(logData);
                }
            }
        } catch (err) {
            // logs/ folder doesn't exist yet
        }

        // Sort by timestamp if available, newest first
        return logs.sort((a, b) => {
            const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return timeB - timeA;
        });
    }

    /**
     * Save global glossary to terminology/glossary.json
     */
    async saveGlossary(data: any): Promise<void> {
        if (!this.handle) return;
        try {
            const termDir = await this.handle.getDirectoryHandle('terminology', { create: true });
            const fileHandle = await termDir.getFileHandle('glossary.json', { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(JSON.stringify(data, null, 2));
            await writable.close();
        } catch (err) {
            console.error('Failed to save glossary:', err);
        }
    }

    /**
     * Load global glossary from terminology/glossary.json
     */
    async loadGlossary(): Promise<any | null> {
        if (!this.handle) return null;
        try {
            const termDir = await this.handle.getDirectoryHandle('terminology', { create: false });
            const fileHandle = await termDir.getFileHandle('glossary.json', { create: false });
            const file = await fileHandle.getFile();
            const text = await file.text();
            return JSON.parse(text);
        } catch (err) {
            // terminology/glossary.json doesn't exist yet
            return null;
        }
    }

    /**
     * US-19-16: Lazy-load single file with full content (sourceText, originalFile, etc).
     * Used by ensureFileContentLoaded() boundary in hooks/useFileSystem.ts.
     *
     * Codex Stage 1 round 1 concern #4: NEVER load other files in same project — that
     * defeats lazy loading. Strategy: scan only .state.json files first to find matching
     * fileId, then read ONLY that file's heavy artifacts (.source.txt, .original.docx, etc).
     */
    async loadFileFull(projectId: string, fileId: string): Promise<any | null> {
        if (!this.handle) return null;

        const findInDir = async (dirHandle: FileSystemDirectoryHandle): Promise<any | null> => {
            try {
                for await (const entry of (dirHandle as any).values()) {
                    if (entry.kind !== 'directory' || !entry.name.startsWith('project_')) continue;
                    const projectJson = await this.readFile(entry, 'project.json');
                    if (!projectJson || projectJson.id !== projectId) continue;
                    // Found project — scan ONLY .state.json files to find target file's base name
                    try {
                        const filesDir = await entry.getDirectoryHandle('files', { create: false });
                        let targetBase: string | null = null;
                        let targetState: any = null;
                        for await (const fileEntry of (filesDir as any).values()) {
                            if (fileEntry.kind !== 'file' || !fileEntry.name.endsWith('.state.json')) continue;
                            const state = await this.readFile(filesDir, fileEntry.name);
                            if (state && state.id === fileId) {
                                targetBase = fileEntry.name.slice(0, -'.state.json'.length);
                                targetState = state;
                                break;
                            }
                        }
                        if (!targetBase || !targetState) return null;

                        // Read ONLY this file's heavy artifacts (parallel for speed)
                        const [source, translation, originalFile, originalDocxFallback, paragraphMapJson, auditJson, auditCsv, semanticJson, semanticCsv] = await Promise.all([
                            this.readRawFile(filesDir, `${targetBase}.source.txt`).catch(() => null),
                            this.readRawFile(filesDir, `${targetBase}.translation.txt`).catch(() => null),
                            this.readBinaryFile(filesDir, `${targetBase}.original.${targetState.originalFormat || 'docx'}`).catch(() => null),
                            this.readBinaryFile(filesDir, `${targetBase}.original.docx`).catch(() => null),
                            this.readFile(filesDir, `${targetBase}.paragraphMap.json`).catch(() => null),
                            this.readFile(filesDir, `${targetBase}.audit.json`).catch(() => null),
                            this.readRawFile(filesDir, `${targetBase}.audit.csv`).catch(() => null),
                            this.readFile(filesDir, `${targetBase}.semantic_match.json`).catch(() => null),
                            this.readRawFile(filesDir, `${targetBase}.semantic_match.csv`).catch(() => null),
                        ]);

                        const file = this.reassembleFile({
                            state: targetState,
                            source: source || undefined,
                            translation: translation || undefined,
                            originalFile: originalFile || originalDocxFallback || undefined,
                            paragraphMapJson: paragraphMapJson || undefined,
                            auditJson: auditJson || undefined,
                            auditCsv: auditCsv || undefined,
                            semanticJson: semanticJson || undefined,
                            semanticCsv: semanticCsv || undefined,
                        }, 'full');
                        return file;
                    } catch (e) { /* no files dir */ }
                    return null;
                }
            } catch (e) { /* dir not present */ }
            return null;
        };

        // Search 3 known locations in parallel
        const [root, translating, training] = await Promise.all([
            findInDir(this.handle),
            this.handle.getDirectoryHandle('translating', { create: false })
                .then(d => findInDir(d))
                .catch(() => null),
            this.handle.getDirectoryHandle('training', { create: false })
                .then(d => findInDir(d))
                .catch(() => null),
        ]);
        return root || translating || training || null;
    }

    /**
     * Read all projects - supports root (legacy), translating/, and training/ folders.
     *
     * US-19-16: mode='metadata' reads only project.json + .state.json (skip heavy artifacts).
     *           mode='full' (default) loads everything (preserves existing callers).
     */
    async loadProjects(mode: 'metadata' | 'full' = 'full'): Promise<any[]> {
        if (!this.handle) return [];
        const projectMap = new Map<string, any>();

        const addProjects = (list: any[]) => {
            for (const p of list) {
                if (p.id) {
                    projectMap.set(p.id, p);
                }
            }
        };

        // Parallel scan of 3 directories — small win but free (Codex audit priority #5)
        const [rootProjects, translatingProjects, trainingProjects] = await Promise.all([
            this.scanDirectoryForProjects(this.handle, mode),
            this.handle.getDirectoryHandle('translating', { create: false })
                .then(dir => this.scanDirectoryForProjects(dir, mode))
                .catch(() => []),
            this.handle.getDirectoryHandle('training', { create: false })
                .then(dir => this.scanDirectoryForProjects(dir, mode))
                .catch(() => []),
        ]);

        addProjects(rootProjects);
        addProjects(translatingProjects);
        addProjects(trainingProjects);

        return Array.from(projectMap.values());
    }

    private async scanDirectoryForProjects(dirHandle: FileSystemDirectoryHandle, mode: 'metadata' | 'full' = 'full'): Promise<any[]> {
        const projects: any[] = [];
        for await (const entry of (dirHandle as any).values()) {
            if (entry.kind === 'directory' && entry.name.startsWith('project_')) {
                try {
                    const projectJson = await this.readFile(entry, 'project.json');
                    if (projectJson) {
                        const files: any[] = [];
                        try {
                            const filesDir = await entry.getDirectoryHandle('files', { create: false });
                            const fileGroups = await this.groupFilesByBase(filesDir, mode);

                            // Reassemble each file group
                            for (const [, parts] of fileGroups) {
                                const file = this.reassembleFile(parts, mode);
                                if (file) files.push(file);
                            }
                        } catch (err) { }

                        // Return combined data
                        if (files.length > 0) {
                            projects.push({ ...projectJson, files });
                        } else if (projectJson.files && Array.isArray(projectJson.files)) {
                            projects.push(projectJson);
                        } else {
                            projects.push({ ...projectJson, files: [] });
                        }
                    }
                } catch (err) {
                    console.warn(`Failed to load project from ${entry.name}:`, err);
                }
            }
        }
        return projects;
    }

    /**
     * Scan a files/ directory and group entries by base name.
     * e.g. "Vi_6-4-C.txt.2b5b216e" groups .state.json + .source.txt + .audit.json etc.
     */
    private async groupFilesByBase(filesDir: FileSystemDirectoryHandle, mode: 'metadata' | 'full' = 'full'): Promise<Map<string, FileParts>> {
        const fileGroups = new Map<string, FileParts>();

        const ensureGroup = (base: string) => {
            if (!fileGroups.has(base)) fileGroups.set(base, {});
            return fileGroups.get(base)!;
        };

        // Extension-to-field mapping for clean dispatch
        // US-19-16: heavy=true → SKIP read in metadata mode (save 1MB+ disk I/O per file)
        const extensionMap: { ext: string; field: keyof FileParts; reader: 'json' | 'raw' | 'binary'; heavy: boolean }[] = [
            { ext: '.state.json',          field: 'state',            reader: 'json',   heavy: false }, // always load
            { ext: '.source.txt',          field: 'source',           reader: 'raw',    heavy: true  },
            { ext: '.translation.txt',     field: 'translation',      reader: 'raw',    heavy: true  },
            { ext: '.audit.json',          field: 'auditJson',        reader: 'json',   heavy: true  },
            { ext: '.audit.csv',           field: 'auditCsv',         reader: 'raw',    heavy: true  },
            { ext: '.semantic_match.json', field: 'semanticJson',     reader: 'json',   heavy: true  },
            { ext: '.semantic_match.csv',  field: 'semanticCsv',      reader: 'raw',    heavy: true  },
            { ext: '.original.docx',       field: 'originalFile',     reader: 'binary', heavy: true  },
            { ext: '.original.pptx',       field: 'originalFile',     reader: 'binary', heavy: true  },
            { ext: '.paragraphMap.json',   field: 'paragraphMapJson', reader: 'json',   heavy: true  },
        ];

        for await (const fileEntry of (filesDir as any).values()) {
            if (fileEntry.kind !== 'file') continue;

            let matched = false;
            for (const { ext, field, reader, heavy } of extensionMap) {
                if (fileEntry.name.endsWith(ext)) {
                    const base = fileEntry.name.slice(0, -ext.length);
                    const group = ensureGroup(base);
                    // US-19-16: skip heavy artifacts in metadata mode
                    if (mode === 'metadata' && heavy) {
                        matched = true;
                        break;
                    }
                    if (reader === 'json') {
                        (group as any)[field] = await this.readFile(filesDir, fileEntry.name);
                    } else if (reader === 'raw') {
                        (group as any)[field] = await this.readRawFile(filesDir, fileEntry.name);
                    } else {
                        (group as any)[field] = await this.readBinaryFile(filesDir, fileEntry.name) || undefined;
                    }
                    matched = true;
                    break;
                }
            }

            // Legacy fallback: any .json not matched above (always load — small)
            if (!matched && fileEntry.name.endsWith('.json')) {
                const base = fileEntry.name.replace('.json', '');
                ensureGroup(base).legacy = await this.readFile(filesDir, fileEntry.name);
            }
        }

        return fileGroups;
    }

    /**
     * Reassemble a TranslationFile from its disk parts.
     * Returns null if no usable data found.
     *
     * US-19-16: mode='metadata' → contentLoaded=false. Heavy fields not assigned (parts.* are undefined).
     */
    private reassembleFile(parts: FileParts, mode: 'metadata' | 'full' = 'full'): any | null {
        if (parts.state) {
            // === NEW FORMAT: .state.json as base ===
            const file = { ...parts.state };

            // Binary: original Office file + paragraph map
            if (parts.originalFile) file.originalFile = parts.originalFile;
            if (parts.paragraphMapJson && Array.isArray(parts.paragraphMapJson)) {
                file.docxParagraphMap = parts.paragraphMapJson;
            }

            // Text data
            if (parts.source) file.sourceText = parts.source;
            if (parts.translation) file.aiOutput = parts.translation.split('\n');

            // Audit: JSON preferred > CSV legacy
            if (parts.auditJson) {
                file.auditResult = parts.auditJson;
                file.auditStatus = 'done';
            } else if (parts.auditCsv) {
                file.auditRawOutput = parts.auditCsv;
                const isQCFormat = parts.auditCsv.includes('sourceIndex|score|issues|suggestions')
                    || !parts.auditCsv.includes('humanIndices');
                if (isQCFormat) {
                    const totalSegments = file.sourceText
                        ? file.sourceText.split('\n').filter((l: string) => l.trim()).length
                        : 0;
                    file.auditResult = parseCSVQualityCheckResponse(parts.auditCsv, totalSegments);
                } else {
                    file.auditResult = parseCSVAuditResponse(parts.auditCsv);
                }
                file.auditStatus = 'done';
            }

            // Semantic match: JSON preferred > CSV legacy
            if (parts.semanticJson) {
                file.semanticMatchResult = {
                    ...parts.semanticJson,
                    rawOutput: parts.semanticCsv || ''
                };
            } else if (parts.semanticCsv) {
                const viSegments = file.sourceText ? file.sourceText.split('\n') : [];
                console.warn('[FileSystem] Falling back to CSV re-parse for semantic match (may lose mappings)');
                const mappings = parseOutput(parts.semanticCsv, viSegments, []);
                file.semanticMatchResult = {
                    mappings,
                    alignedHumanSegments: mappings.map((m: any) => m.humanText),
                    rawOutput: parts.semanticCsv,
                    unmatchedCount: mappings.filter((m: any) => m.isUnmatched).length
                };
            }

            // US-19-16: tag content load state. metadata mode → contentLoaded=false (heavy fields skipped).
            file.contentLoaded = mode === 'full';

            return file;
        }

        if (parts.legacy) {
            // === LEGACY FORMAT: file_{id}.json ===
            // Legacy single-JSON has all fields inline → effectively full content
            return { ...parts.legacy, contentLoaded: true };
        }

        return null;
    }

    private async readFile(dirHandle: FileSystemDirectoryHandle, fileName: string): Promise<any> {
        try {
            const text = await this.readRawFile(dirHandle, fileName);
            return text ? JSON.parse(text) : null;
        } catch (err) {
            return null;
        }
    }

    private async readRawFile(dirHandle: FileSystemDirectoryHandle, fileName: string): Promise<string | null> {
        try {
            const fileHandle = await dirHandle.getFileHandle(fileName);
            const file = await fileHandle.getFile();
            return await file.text();
        } catch (err) {
            return null;
        }
    }

    /**
     * Delete a project folder from disk.
     * Scans root, translating, and training folders.
     */
    async deleteProjectFolder(projectId: string, projectName?: string): Promise<void> {
        if (!this.handle) return;

        const dirsToScan = [this.handle];
        try { dirsToScan.push(await this.handle.getDirectoryHandle('translating')); } catch { }
        try { dirsToScan.push(await this.handle.getDirectoryHandle('training')); } catch { }

        for (const dir of dirsToScan) {
            const foldersToDelete: string[] = [];

            for await (const entry of (dir as any).values()) {
                if (entry.kind === 'directory' && entry.name.startsWith('project_')) {
                    if (entry.name === `project_${projectId}`) {
                        foldersToDelete.push(entry.name);
                        continue;
                    }
                    try {
                        const projectJson = await this.readFile(entry, 'project.json');
                        if (projectJson && projectJson.id === projectId) {
                            foldersToDelete.push(entry.name);
                        }
                    } catch (err) { }
                }
            }

            for (const folderName of foldersToDelete) {
                try {
                    await dir.removeEntry(folderName, { recursive: true });
                    console.log(`[DeleteProject] Removed folder: ${folderName} in ${dir.name}`);
                } catch (err) {
                    console.error(`[DeleteProject] Failed to remove ${folderName}:`, err);
                }
            }
        }
    }

    /**
     * Delete a single file from disk within its project folder.
     * Finds the project folder, then removes all related disk artifacts in files/.
     */
    async deleteFileFromDisk(projectId: string, fileId: string, fileName?: string): Promise<void> {
        if (!this.handle) return;

        const dirsToScan = [this.handle];
        try { dirsToScan.push(await this.handle.getDirectoryHandle('translating')); } catch { }
        try { dirsToScan.push(await this.handle.getDirectoryHandle('training')); } catch { }

        for (const dir of dirsToScan) {
            for await (const entry of (dir as any).values()) {
                if (entry.kind !== 'directory' || !entry.name.startsWith('project_')) continue;

                // Check if this is the right project folder
                let isMatch = entry.name === `project_${projectId}`;
                if (!isMatch) {
                    try {
                        const projectJson = await this.readFile(entry, 'project.json');
                        if (projectJson && projectJson.id === projectId) isMatch = true;
                    } catch { }
                }

                if (!isMatch) continue;

                // Found the project folder - scan files/ directory
                try {
                    const filesDir = await entry.getDirectoryHandle('files', { create: false });
                    const filesToDelete: string[] = [];

                    for await (const fileEntry of (filesDir as any).values()) {
                        if (fileEntry.kind !== 'file') continue;

                        // Match by fileId in state.json content, or by filename pattern
                        if (fileEntry.name.endsWith('.state.json')) {
                            const stateData = await this.readFile(filesDir, fileEntry.name);
                            if (stateData && stateData.id === fileId) {
                                // Found matching state file - get the base name to find all related files
                                const base = fileEntry.name.replace('.state.json', '');
                                // Collect all files with this base name
                                for await (const f of (filesDir as any).values()) {
                                    if (f.kind === 'file' && f.name.startsWith(base)) {
                                        filesToDelete.push(f.name);
                                    }
                                }
                                break;
                            }
                        }
                    }

                    // Delete all matched files
                    for (const name of filesToDelete) {
                        try {
                            await filesDir.removeEntry(name);
                            console.log(`[DeleteFile] Removed: ${name} from ${entry.name}/files/`);
                        } catch (err) {
                            console.error(`[DeleteFile] Failed to remove ${name}:`, err);
                        }
                    }

                    if (filesToDelete.length > 0) {
                        console.log(`[DeleteFile] Deleted ${filesToDelete.length} artifacts for file ${fileId}`);
                        return; // Done
                    }
                } catch { }
            }
        }
    }

    /**
     * Disconnect from the current folder
     */
    async disconnect(): Promise<void> {
        this.handle = null;
        await this.deleteHandleFromDB();
    }

    // === IndexedDB Helpers ===

    private async getHandleFromDB(): Promise<FileSystemDirectoryHandle | null> {
        return new Promise((resolve) => {
            const request = indexedDB.open(DB_NAME, 1);
            request.onupgradeneeded = () => {
                request.result.createObjectStore(STORE_NAME);
            };
            request.onsuccess = () => {
                const db = request.result;
                const tx = db.transaction(STORE_NAME, 'readonly');
                const store = tx.objectStore(STORE_NAME);
                const getRequest = store.get(HANDLE_KEY);
                getRequest.onsuccess = () => resolve(getRequest.result || null);
                getRequest.onerror = () => resolve(null);
            };
            request.onerror = () => resolve(null);
        });
    }

    private async saveHandleToDB(handle: FileSystemDirectoryHandle): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, 1);
            request.onsuccess = () => {
                const db = request.result;
                const tx = db.transaction(STORE_NAME, 'readwrite');
                const store = tx.objectStore(STORE_NAME);
                store.put(handle, HANDLE_KEY);
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            };
            request.onerror = () => reject(request.error);
        });
    }

    private async deleteHandleFromDB(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, 1);
            request.onsuccess = () => {
                const db = request.result;
                const tx = db.transaction(STORE_NAME, 'readwrite');
                const store = tx.objectStore(STORE_NAME);
                store.delete(HANDLE_KEY);
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            };
            request.onerror = () => reject(request.error);
        });
    }
}

export const fileSystemPersistence = new FileSystemPersistenceService();
